import { WHATSAPP_URL } from '../data/siteData';
import { PageHero } from '../components/UI';
import { ValueAddedIcon, getAccentColor } from '../components/ValueAddedIcons';

const services = [
  {
    key: 'airport',
    title: 'Airport Pickup',
    desc: "Stress-free arrival with a trusted Travizon driver waiting for you at arrivals. Available in UK, Canada, Australia, Germany & more.",
    features: ['Pre-booked & confirmed 48hrs prior','Flight tracking included','WhatsApp contact shared before travel','Available 24/7 including holidays'],
  },
  {
    key: 'insurance',
    title: 'Insurance Activation',
    desc: "We don't just arrange your insurance — we activate it, confirm your coverage documents, and ensure it meets your visa requirements.",
    features: ['Same-day activation available','Embassy-compliant documents issued','Schengen, UK, Australia, Canada covered','Policy review & gap analysis'],
  },
  {
    key: 'sim',
    title: 'SIM Card Activation',
    desc: "Arrive with a working local number, data plan, and WhatsApp active. No roaming charges, no confusion at the airport.",
    features: ['Pre-activated before arrival','Best local carrier selected per country','Data + calls bundle included','Pakistan roaming option available'],
  },
  {
    key: 'bank',
    title: 'Bank Account Processing',
    desc: "Opening a bank account abroad is complex. We guide you through the entire process with our bank contacts in each country.",
    features: ['Account opened before or after arrival','Digital banks (Wise, Revolut) setup','Student & international accounts','IBAN & routing number arranged'],
  },
  {
    key: 'police',
    title: 'Police Verification',
    desc: "Required for many visa applications and migration pathways. We manage the complete process including form submission and tracking.",
    features: ['Pakistan police character certificate','NADRA attestation support','Embassy-standard formatting','Express processing available'],
  },
  {
    key: 'attestation',
    title: 'Document Attestation',
    desc: "Complete attestation chain — Board, IBCC, HEC, MOFA, and Apostille — handled by our dedicated attestation team.",
    features: ['Board / Matric / Intermediate','IBCC equivalency certificates','HEC degree attestation','MOFA attestation & Apostille'],
  },
  {
    key: 'contract',
    title: 'Accommodation Contract Clearance',
    desc: "We review your accommodation contract before you sign — checking legal terms, deposit conditions, and tenant rights.",
    features: ['Full contract legal review','Deposit & exit clause check','Tenant rights briefing','Renegotiation support if needed'],
  },
];

const attestationSteps = [
  { step: '01', org: 'Board of Education', color: '#3B82F6', desc: 'Matric & Intermediate certificate attestation from respective Provincial Board' },
  { step: '02', org: 'IBCC',               color: '#8B5CF6', desc: 'Inter Board Committee of Chairmen equivalency for O/A levels and foreign degrees' },
  { step: '03', org: 'HEC',                color: '#10B981', desc: 'Higher Education Commission attestation for degrees and transcripts' },
  { step: '04', org: 'MOFA',               color: '#F59E0B', desc: 'Ministry of Foreign Affairs attestation — required for international use' },
  { step: '05', org: 'Apostille',          color: '#1A7A8A', desc: 'International authentication under the Hague Convention for 120+ countries' },
];

export default function ValueAdded({ setPage }) {
  const wa = (title) => window.open(`${WHATSAPP_URL}?text=Hi, I need information about your ${encodeURIComponent(title)} service`, '_blank');

  return (
    <div>
      <PageHero
        breadcrumb="Value Added Services"
        title="Beyond the Visa."
        subtitle="Travizon supports you long after your visa is approved — from airport pickup on Day 1 to bank accounts, SIM cards, and document attestation."
      />

      {/* ── SERVICES GRID ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <span className="section-tag">Full Journey Support</span>
            <h2 className="font-display text-4xl sm:text-5xl text-navy font-semibold mt-1 mb-3">We're With You After Landing Too</h2>
            <p className="text-gray-500 max-w-xl mx-auto leading-relaxed">Most consultancies stop at the visa stamp. Travizon's value-added services cover every practical step of settling in abroad.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc) => {
              const accent = getAccentColor(svc.key);
              return (
                <div
                  key={svc.key}
                  onClick={() => wa(svc.title)}
                  className="group bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-[0_20px_60px_rgba(11,31,58,0.13)] hover:-translate-y-1.5 transition-all duration-300 cursor-pointer"
                >
                  {/* Top accent bar */}
                  <div className="h-1 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" style={{ background: `linear-gradient(90deg, ${accent}, ${accent}99)` }} />
                  <div className="h-0.5 w-full" style={{ background: `linear-gradient(90deg, ${accent}33, transparent)` }} />

                  <div className="p-7">
                    {/* SVG Icon — 56×56 */}
                    <div className="w-14 h-14 mb-5 drop-shadow-lg">
                      <ValueAddedIcon type={svc.key} size={56} />
                    </div>

                    <h3 className="font-body font-bold text-navy text-lg mb-2">{svc.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-5">{svc.desc}</p>

                    {/* Feature list with colored check circles */}
                    <ul className="space-y-2.5 mb-6">
                      {svc.features.map(f => (
                        <li key={f} className="flex items-start gap-2.5 text-xs text-gray-500">
                          <svg className="w-4 h-4 flex-shrink-0 mt-0.5" viewBox="0 0 16 16" fill="none">
                            <circle cx="8" cy="8" r="7" fill={accent} opacity="0.12"/>
                            <path d="M5 8l2.5 2.5 4-4" stroke={accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          {f}
                        </li>
                      ))}
                    </ul>

                    {/* Arrow CTA */}
                    <div
                      className="flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-all duration-200"
                      style={{ color: accent }}
                    >
                      Enquire on WhatsApp
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                      </svg>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── ATTESTATION DEEP DIVE ── */}
      <section className="py-16 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="section-tag">Document Attestation</span>
              <h2 className="font-display text-4xl text-navy font-semibold mt-1 mb-5">The Complete Attestation Chain</h2>
              <p className="text-gray-500 leading-relaxed mb-8">Document attestation is one of the most confusing parts of any international application. Travizon handles the complete chain — from Board to Apostille — so your documents are accepted without question.</p>

              <div className="flex flex-col gap-0">
                {attestationSteps.map(({ step, org, color, desc }, i, arr) => (
                  <div key={step} className="flex gap-4">
                    <div className="flex flex-col items-center w-10 flex-shrink-0">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center font-display text-sm font-bold text-white shadow-md" style={{ background: color }}>
                        {step}
                      </div>
                      {i < arr.length - 1 && (
                        <div className="w-0.5 flex-1 min-h-[20px] my-1" style={{ background: `linear-gradient(${color}, transparent)`, opacity: 0.3 }}/>
                      )}
                    </div>
                    <div className="pb-6">
                      <div className="font-body font-bold text-navy text-sm mb-0.5">{org}</div>
                      <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-navy rounded-3xl p-10 text-white">
              <div className="w-16 h-16 mb-6 drop-shadow-xl">
                <ValueAddedIcon type="attestation" size={64} />
              </div>
              <h3 className="font-display text-2xl mb-4">Why Get It Right the First Time?</h3>
              <p className="text-white/55 text-sm leading-relaxed mb-7">Incorrect or incomplete attestation is one of the top reasons visa applications are delayed or refused. A missing MOFA stamp or wrong attestation chain can cost you weeks and a rejected application.</p>

              <div className="space-y-4 mb-8">
                {[
                  'Embassy refusals due to attestation errors are 100% avoidable',
                  'Express attestation available for urgent applications',
                  'We track every document at every stage',
                  'Physical collection and delivery available in Lahore',
                ].map(pt => (
                  <div key={pt} className="flex gap-3 items-start">
                    <div className="w-5 h-5 rounded-full bg-teal/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3 h-3 text-teal-light" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </div>
                    <span className="text-white/65 text-sm">{pt}</span>
                  </div>
                ))}
              </div>

              <a
                href={`${WHATSAPP_URL}?text=Hi, I need document attestation services`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-teal to-teal-light text-white font-semibold text-sm px-6 py-3 rounded-xl hover:-translate-y-0.5 transition-all shadow-[0_4px_14px_rgba(26,122,138,0.4)]"
              >
                <svg className="w-4 h-4 fill-white" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347"/>
                </svg>
                Start Attestation Process
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── BUNDLE CTA ── */}
      <section className="py-16 bg-gradient-to-br from-teal to-navy">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-16 h-16 mx-auto mb-5">
            <svg viewBox="0 0 64 64" fill="none" className="w-full h-full">
              <circle cx="32" cy="32" r="32" fill="white" fillOpacity="0.1"/>
              <path d="M20 14h24v13a12 12 0 01-12 12 12 12 0 01-12-12z" fill="white" fillOpacity="0.9"/>
              <path d="M16 14h4M44 14h4M16 18.5c0 4 2.2 7.2 4.3 8.5M48 18.5c0 4-2.2 7.2-4.3 8.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
              <rect x="28" y="39" width="8" height="7" rx="1.5" fill="white" fillOpacity="0.85"/>
              <rect x="22" y="46" width="20" height="3.5" rx="1.5" fill="white"/>
              <path d="M27.5 26.5l2.5 2.5 5.5-5.5" stroke="#1A7A8A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className="font-display text-4xl text-white font-semibold mb-4">Bundle & Save</h2>
          <p className="text-white/65 text-lg mb-8 max-w-xl mx-auto">Combine value-added services with your visa application for discounted rates. Ask about our Full Journey Package.</p>
          <a
            href={`${WHATSAPP_URL}?text=Hi, I'm interested in the Full Journey Package including value-added services`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 bg-white text-navy font-bold text-sm px-8 py-4 rounded-full hover:bg-gray-50 transition-colors shadow-lg"
          >
            <svg className="w-5 h-5 fill-[#25D366]" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347"/>
            </svg>
            Ask About Full Journey Package
          </a>
        </div>
      </section>
    </div>
  );
}
